import { getLast, isNullOrUndefined, pascalCase } from '@afterpay/utils'
import { useApplePaylaterApplePayDisplay, useIsApplePaylaterWithApplePayRepayment, useSavedCards } from '../../state'
import { useTranslation } from '../../utils/convergence'
import { CardBrand, PaymentMethodType } from '@afterpay/types'
import { EnrichedCardDetails, isNewCard, isSavedCard, NewCard, SavedCard, SelectedPaymentMethod } from '../../model'
import { IconType } from '../PaymentMethodIcon'
import { getCardBrandFromCardNumber } from '@afterpaytouch/form/lib/fields/Card/utils'

interface PaymentMethodProps {
  iconType?: IconType
  iconTitle?: string
  paymentMethodName: string
  paymentMethodDesc?: string
  expiryDate?: string
  hasSeparator?: boolean
  cashTag?: string
  creditCardType?: string
}

export const useBuildPaymentMethodProps = (paymentMethod?: SelectedPaymentMethod): PaymentMethodProps | undefined => {
  const savedCards = useSavedCards()
  const { t } = useTranslation()
  const isApplePayRepaymentEnabled = useIsApplePaylaterWithApplePayRepayment()
  const { lastFour, expiryDate } = useApplePaylaterApplePayDisplay()

  if (isNullOrUndefined(paymentMethod)) {
    return
  }
  if (paymentMethod.type === PaymentMethodType.CREDIT_CARD) {
    if (isNewCard(paymentMethod.card)) {
      return transformNewCardToPaymentMethod(paymentMethod.card)
    }
    if (isSavedCard(paymentMethod.card)) {
      return transformSavedCardToPaymentMethod(paymentMethod.card, savedCards)
    }
  }

  if (paymentMethod.type === PaymentMethodType.ACH_BANK_ACCOUNT) {
    return {
      iconType: PaymentMethodType.ACH_BANK_ACCOUNT,
      iconTitle: t('paymentMethod:bankaccount'),
      paymentMethodName: paymentMethod.account?.issuingBank ?? '',
      paymentMethodDesc: paymentMethod.account?.numberMask,
    }
  }

  if (paymentMethod.type === PaymentMethodType.APPLE_PAY) {
    if (isApplePayRepaymentEnabled) {
      return {
        iconType: PaymentMethodType.APPLE_PAY,
        iconTitle: t('paymentMethod:applePay'),
        paymentMethodName: t('paymentMethod:applePay'),
        paymentMethodDesc: lastFour ?? undefined,
        expiryDate: expiryDate,
        hasSeparator: !isNullOrUndefined(lastFour) && !isNullOrUndefined(expiryDate),
      }
    }

    return {
      iconType: PaymentMethodType.APPLE_PAY,
      iconTitle: t('paymentMethod:applePay'),
      paymentMethodName: t('paymentMethod:applePay'),
    }
  }

  if (paymentMethod.type === PaymentMethodType.CASH_APP_PAY) {
    return {
      iconType: PaymentMethodType.CASH_APP_PAY,
      paymentMethodName: t('paymentMethod:cashAppPay'),
      iconTitle: t('paymentMethod:cashAppPay'),
      cashTag: paymentMethod.cashAppPay?.cashtag,
    }
  }
}

function transformSavedCardToPaymentMethod(selectedCard: SavedCard, savedCards: EnrichedCardDetails[]): PaymentMethodProps {
  const token = selectedCard.token
  const card = savedCards.find((savedCard) => savedCard.token === token)
  const charactersToRender = card?.cardBrand === CardBrand.AMEX ? 3 : 4
  const accountNumberMask = getLast(card?.maskedPan, charactersToRender)
  const cardBrandToDisplay = pascalCase(card?.cardBrand?.toString() ?? '')
  return {
    iconType: card?.cardBrand,
    paymentMethodName: cardBrandToDisplay,
    paymentMethodDesc: accountNumberMask,
    expiryDate: card?.expiryDate,
    hasSeparator: true,
    creditCardType: card?.creditCardType,
  }
}

function transformNewCardToPaymentMethod(selectedCard: NewCard): PaymentMethodProps {
  const cardBrand = getCardBrandFromCardNumber(selectedCard.cardNumber)
  const charactersToRender = cardBrand === CardBrand.AMEX ? 3 : 4
  const accountNumberMask = getLast(selectedCard.cardNumber, charactersToRender)
  const cardBrandToDisplay = isNullOrUndefined(cardBrand) ? '' : pascalCase(cardBrand.toString())
  return {
    iconType: cardBrand,
    paymentMethodName: cardBrandToDisplay,
    paymentMethodDesc: accountNumberMask,
    expiryDate: `${selectedCard.cardExpiryMonth}/${selectedCard.cardExpiryYear}`,
    hasSeparator: true,
  }
}
